import type { Logger } from "@expert/logging";

import { setGlobalConfig } from "@asurion-core/headless-chat-web";
import { useEffect, useMemo } from "react";

import { getGlobalConfigFromParams } from "./queryParams";
import { startSimplrSession } from "./sessionHandling";
import { type LogicBuilderGlobalConfig, SIMPLR_PUBLIC_SERVER_DOMAIN } from "./types";

interface SimplrInitializersProps {
    callSid?: string;
    globalConfig?: LogicBuilderGlobalConfig;
    isIdleSession?: boolean;
    logger: Logger;
    sessionId: string;
}

export function useSimplrInitializers({
    globalConfig,
    isIdleSession,
    logger: loggerProp,
    sessionId,
}: SimplrInitializersProps) {
    const isNonprod = import.meta.env.VITE_ENV_TYPE !== "production";

    const queryParams = getGlobalConfigFromParams(isNonprod);

    const customFlowSystemId = queryParams?.customFlowSystemId ?? globalConfig?.customFlowSystemId;
    const publicServerDomain = queryParams?.publicServerDomain ?? SIMPLR_PUBLIC_SERVER_DOMAIN;
    const widgetId = queryParams?.widgetId ?? globalConfig?.widgetId;

    const logger = useMemo(
        () =>
            loggerProp.child({
                customFlowSystemId,
                module: "useSimplrInitializers",
                publicServerDomain,
                queryParams,
                widgetId,
            }),
        [customFlowSystemId, loggerProp, publicServerDomain, queryParams, widgetId],
    );

    useEffect(() => {
        if (!publicServerDomain || !widgetId) {
            return;
        }

        logger.info("Solve Guide | Setting Simplr Global Config");

        setGlobalConfig({
            publicServerDomain,
            widgetId,
            publicServerPrefix: "/chat-api",
            analytics: {
                trackEvent: console.log,
            },
            calculateProgress: true,
            customFlowSystemId,
            handleError: ({ error, errorLevel }) => {
                switch (errorLevel) {
                    case "warning":
                        // filter out the warnings, log everything else
                        break;
                    default:
                        logger.warn({ error, errorLevel }, "Solve Guide | Received error from Simplr logic builder");
                        break;
                }
            },
        });

        logger.info("Solve Guide | Calling start session with Simplr immediately on session start");
        startSimplrSession({ sessionId, metadata: {}, isIdleSession });
        // oxlint-disable-next-line exhaustive-deps
    }, [sessionId]);
}
